import { createEnv } from '@t3-oss/env-nextjs'
import { z } from 'zod'

/**
 * Single source of truth for all environment variables.
 *
 * server  — available only during SSR / build (never sent to the browser).
 * client  — inlined into the browser bundle by Next.js at build time.
 *           Values come from the `env` block in next.config.js, which maps
 *           the plain server vars to NEXT_PUBLIC_* so .env needs no duplicates.
 *
 * If a required variable is missing the app will throw at startup with a clear
 * error message instead of silently failing at runtime.
 *
 * To skip validation (e.g. in CI lint steps where env is not available):
 *   SKIP_ENV_VALIDATION=1 npm run lint
 */
export const env = createEnv({
    skipValidation: !!process.env.SKIP_ENV_VALIDATION,

    // ------------------------------------------------------------------ server
    server: {
        ASSET_PREFIX: z.string(),
        BASE_PATH: z.string(),
        BASE_URL: z.string().min(1),
        BFF_BASE_URL: z.string().min(1),
        BUILD_ID: z.string().optional(),
        ENV: z.enum(['development', 'staging', 'production']),
        FASTLANE_BASE_URL: z.string().min(1),
        FASTLANE_INTERNAL_BASE_URL: z.string().min(1),
        GTM_CONTAINER_ID: z.string(),
        SENTRY_AUTH_TOKEN: z.string().optional(),
        SENTRY_DSN: z.string().optional(),
        SENTRY_ORG: z.string().optional(),
        SENTRY_PROJECT: z.string().optional(),
        SENTRY_URL: z.string().optional(),
        TEST_BASE_URL: z.string().min(1),
        GOOGLE_SITE_VERIFICATION: z.string(),
        GOOGLE_MAPS_KEY: z.string(),
        GOOGLE_EMBEDDED_MAPS_KEY: z.string(),
        HTG_PUBLIC_URL: z.string().min(1),
        GYG_PARTNER_ID: z.string().optional(),
        FIREBASE_API_KEY: z.string().optional(),
        FIREBASE_APP_ID: z.string().optional(),
        FIREBASE_MESSAGING_SENDER_ID: z.string().optional(),
        FIREBASE_PROJECT_ID: z.string().optional(),
        FIREBASE_AUTH_DOMAIN: z.string().optional(),
        FIREBASE_STORAGE_BUCKET: z.string().optional(),
    },

    // ------------------------------------------------------------------ client
    // These are statically inlined by Next.js. Their values are set in the
    // `env` block of next.config.js — no NEXT_PUBLIC_* entries needed in .env.
    client: {
        NEXT_PUBLIC_ASSET_PREFIX: z.string(),
        NEXT_PUBLIC_BASE_PATH: z.string(),
        // NEXT_PUBLIC_BASE_URL removed — BASE_URL is server-only + runtime.
        NEXT_PUBLIC_ENV: z.enum(['development', 'staging', 'production']),
        NEXT_PUBLIC_GTM_CONTAINER_ID: z.string(),
        NEXT_PUBLIC_SENTRY_DSN: z.string().optional(),
        NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION: z.string(),
        NEXT_PUBLIC_GOOGLE_MAPS_KEY: z.string(),
        NEXT_PUBLIC_GOOGLE_EMBEDDED_MAPS_KEY: z.string(),
        NEXT_PUBLIC_HTG_PUBLIC_URL: z.string().min(1),
        NEXT_PUBLIC_GYG_PARTNER_ID: z.string().optional(),
        NEXT_PUBLIC_MEDIA_BASE_URL: z.string().min(1),
        NEXT_PUBLIC_FIREBASE_API_KEY: z.string().optional(),
        NEXT_PUBLIC_FIREBASE_APP_ID: z.string().optional(),
        NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: z.string().optional(),
        NEXT_PUBLIC_FIREBASE_PROJECT_ID: z.string().optional(),
        NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN: z.string().optional(),
        NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET: z.string().optional(),
    },

    // ------------------------------------------------------------------ runtimeEnv
    // Server vars: read directly from process.env.
    // Client vars: read from process.env.NEXT_PUBLIC_* which Next.js has already
    //              inlined from the `env` block in next.config.js.
    runtimeEnv: {
        ASSET_PREFIX: process.env.ASSET_PREFIX,
        BASE_PATH: process.env.BASE_PATH,
        BASE_URL: process.env.BASE_URL,
        BFF_BASE_URL: process.env.BFF_BASE_URL,
        BUILD_ID: process.env.BUILD_ID,
        ENV: process.env.ENV,
        FASTLANE_BASE_URL: process.env.FASTLANE_BASE_URL,
        FASTLANE_INTERNAL_BASE_URL: process.env.FASTLANE_INTERNAL_BASE_URL,
        GTM_CONTAINER_ID: process.env.GTM_CONTAINER_ID,
        SENTRY_AUTH_TOKEN: process.env.SENTRY_AUTH_TOKEN,
        SENTRY_DSN: process.env.SENTRY_DSN,
        SENTRY_ORG: process.env.SENTRY_ORG,
        SENTRY_PROJECT: process.env.SENTRY_PROJECT,
        SENTRY_URL: process.env.SENTRY_URL,
        TEST_BASE_URL: process.env.TEST_BASE_URL,
        GOOGLE_SITE_VERIFICATION: process.env.GOOGLE_SITE_VERIFICATION,
        GOOGLE_MAPS_KEY: process.env.GOOGLE_MAPS_KEY,
        GOOGLE_EMBEDDED_MAPS_KEY: process.env.GOOGLE_EMBEDDED_MAPS_KEY,
        HTG_PUBLIC_URL: process.env.HTG_PUBLIC_URL,
        GYG_PARTNER_ID: process.env.GYG_PARTNER_ID,
        FIREBASE_API_KEY: process.env.FIREBASE_API_KEY,
        FIREBASE_APP_ID: process.env.FIREBASE_APP_ID,
        FIREBASE_MESSAGING_SENDER_ID: process.env.FIREBASE_MESSAGING_SENDER_ID,
        FIREBASE_PROJECT_ID: process.env.FIREBASE_PROJECT_ID,
        FIREBASE_AUTH_DOMAIN: process.env.FIREBASE_AUTH_DOMAIN,
        FIREBASE_STORAGE_BUCKET: process.env.FIREBASE_STORAGE_BUCKET,

        NEXT_PUBLIC_ASSET_PREFIX: process.env.NEXT_PUBLIC_ASSET_PREFIX,
        NEXT_PUBLIC_BASE_PATH: process.env.NEXT_PUBLIC_BASE_PATH,
        NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
        NEXT_PUBLIC_GTM_CONTAINER_ID: process.env.NEXT_PUBLIC_GTM_CONTAINER_ID,
        NEXT_PUBLIC_SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
        NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION: process.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION,
        NEXT_PUBLIC_GOOGLE_MAPS_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY,
        NEXT_PUBLIC_GOOGLE_EMBEDDED_MAPS_KEY: process.env.NEXT_PUBLIC_GOOGLE_EMBEDDED_MAPS_KEY,
        NEXT_PUBLIC_HTG_PUBLIC_URL: process.env.NEXT_PUBLIC_HTG_PUBLIC_URL,
        NEXT_PUBLIC_GYG_PARTNER_ID: process.env.NEXT_PUBLIC_GYG_PARTNER_ID,
        NEXT_PUBLIC_MEDIA_BASE_URL: process.env.NEXT_PUBLIC_MEDIA_BASE_URL,
        NEXT_PUBLIC_FIREBASE_API_KEY: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
        NEXT_PUBLIC_FIREBASE_APP_ID: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
        NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
        NEXT_PUBLIC_FIREBASE_PROJECT_ID: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
        NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
        NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
    },
})
